import React, { useState } from 'react'
import { Link, Icon, IconButton, Table, TextArea, TextButton, Button } from '@telus-uds/components-web'
import { Profile, SparkleFilled, ThumbsDownFill, ThumbsUpFill } from '@telus-uds/palette-allium/build/web/icons'
import {
  MessageTagWrapper,
  MessageTagIconWrapper,
  MessageBubble,
  SourcesContainer,
  SourceItem,
  FeedbackContainer,
  FeedbackText,
  FeedbackTextSubmitted,
  FeedbackRow,
  FeedbackActions,
  FeedbackThumbDownOffset,
  FeedbackCommentContainer,
  FeedbackCommentField,
  FeedbackCommentActions,
  TableScrollWrapper,
  TableMinWidthWrapper,
  TableCellContent,
  hexColors,
} from './Styles'
import { AssistantMessage, Message } from './types'
import type { FeedbackPayload } from './chatbotUtils'

const getFeedbackIconButtonTokens = (isSelected: boolean) => ({
  borderColor: 'transparent',
  iconColor: String(hexColors(isSelected ? 'Accessible Green' : 'Border Grey')),
})

/** Parses a markdown table row into cell strings, handling escaped pipes (\|) */
const parseTableRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\||\|$/g, '') // Remove leading/trailing |
    .split(/(?<!\\)\|/) // Split only on unescaped |
    .map((cell) => cell.trim().replace(/\\\|/g, '|')) // Restore escaped pipes within each cell

/** Returns true if the line is a markdown table separator row (e.g. |---|:---:|---:| ) */
const isTableSeparator = (line: string): boolean => /^\|(?:\s*:?-+:?\s*\|)+$/.test(line.trim())

/**
 * Renders markdown content supporting:
 * - Bold text (**text** or __text__)
 * - Headers (# Heading, ## Heading, etc.)
 * - Unordered lists (-, *, or + at line start)
 * - Line breaks and paragraphs
 * - Tables (with escaped-pipe support)
 */
const renderMarkdownContent = (text: string, tableAccessibilityLabel: string) => {
  if (!text || typeof text !== 'string') return text

  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    // Handle markdown tables — detect a line starting and ending with |
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i])
        i++
      }

      // Need at least a header row + separator row
      if (tableLines.length >= 2 && isTableSeparator(tableLines[1])) {
        const headers = parseTableRow(tableLines[0])
        const dataRows = tableLines
          .slice(2)
          .filter((row) => !isTableSeparator(row))
          .map(parseTableRow)

        elements.push(
          <TableScrollWrapper key={`table-${i}`} role="region" tabIndex={0} aria-label={tableAccessibilityLabel}>
            <TableMinWidthWrapper>
              <Table>
                <Table.Header>
                  {headers.map((header, idx) => (
                    <Table.Cell key={idx}>
                      <TableCellContent>{renderBoldText(header)}</TableCellContent>
                    </Table.Cell>
                  ))}
                </Table.Header>
                <Table.Body>
                  {dataRows.map((row, rowIdx) => {
                    // Pad short rows with empty strings so every row has the same
                    // number of cells as the header, keeping columns aligned.
                    const paddedRow =
                      row.length < headers.length ? [...row, ...Array(headers.length - row.length).fill('')] : row
                    return (
                      <Table.Row key={rowIdx}>
                        {paddedRow.map((cell, cellIdx) => (
                          <Table.Cell key={cellIdx}>
                            <TableCellContent>{renderBoldText(cell)}</TableCellContent>
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table>
            </TableMinWidthWrapper>
          </TableScrollWrapper>
        )
        continue
      }

      // Not a valid table — render each line as plain text to avoid re-entering
      // the table detection branch (which would cause an infinite loop)
      tableLines.forEach((tl, tlIdx) => {
        elements.push(<p key={`fallback-${i}-${tlIdx}`}>{renderBoldText(tl)}</p>)
      })
      continue
    }

    // Handle headers
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/)
    if (headerMatch) {
      const level = Math.min(headerMatch[1].length, 6) as 1 | 2 | 3 | 4 | 5 | 6
      const content = headerMatch[2]
      const Tag = `h${level}` as const
      elements.push(
        <Tag key={`header-${i}`} style={{ marginTop: '12px', marginBottom: '8px', fontWeight: 'bold' }}>
          {renderBoldText(content)}
        </Tag>
      )
      i++
      continue
    }

    // Handle list items
    const listMatch = trimmed.match(/^[-*+]\s+(.+)$/)
    if (listMatch) {
      const items: string[] = []
      while (i < lines.length) {
        const currentLine = lines[i].trim()
        const currentListMatch = currentLine.match(/^[-*+]\s+(.+)$/)
        if (currentListMatch) {
          items.push(currentListMatch[1])
          i++
        } else if (currentLine === '') {
          i++
          break
        } else {
          break
        }
      }
      elements.push(
        <ul key={`list-${i}`} style={{ marginLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
          {items.map((item, idx) => (
            <li key={idx}>{renderBoldText(item)}</li>
          ))}
        </ul>
      )
      continue
    }

    // Handle empty lines (paragraph breaks) — emit a blank line for visual spacing
    if (trimmed === '') {
      // Only add a spacer if the previous element wasn't already a spacer
      const lastEl = elements[elements.length - 1]
      const lastKey = lastEl && (lastEl as React.ReactElement)?.key
      if (typeof lastKey !== 'string' || !lastKey.startsWith('br-')) {
        elements.push(<br key={`br-${i}`} />)
      }
      i++
      continue
    }

    // Regular paragraph
    elements.push(
      <React.Fragment key={`para-${i}`}>
        {renderBoldText(line)}
        <br />
      </React.Fragment>
    )
    i++
  }

  return elements.length > 0 ? elements : text
}

/**
 * Renders bold text with support for **text** or __text__ markdown syntax
 */
const renderBoldText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g)
  return parts.map((part, index) => {
    if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

export interface MessageItemProps {
  message: Message
  tableAccessibilityLabel?: string
  sourcesLabel?: string
  feedbackText?: string
  addCommentCtaText?: string
  feedbackConfirmationText?: string
  feedbackPlaceholderText?: string
  commentSubmitLabel?: string
  commentCancelLabel?: string
  thumbsUpLabel?: string
  thumbsDownLabel?: string
  onFeedback?: (payload: FeedbackPayload) => void
}

/**
 * MessageItem Component
 *
 * Renders a single message in the chat with:
 * - Welcome or user/assistant tags
 * - Message content with bold markdown support
 * - Citation sources when available
 * - Message timestamp
 *
 * @param props Message rendering configuration
 * @returns React element representing a single chat message
 */
const MessageItem = ({
  message,
  tableAccessibilityLabel = 'Scrollable table',
  sourcesLabel = 'Sources:',
  feedbackText,
  addCommentCtaText,
  feedbackConfirmationText,
  feedbackPlaceholderText,
  commentSubmitLabel = 'Submit',
  commentCancelLabel = 'Cancel',
  thumbsUpLabel = 'Thumbs up',
  thumbsDownLabel = 'Thumbs down',
  onFeedback,
}: MessageItemProps) => {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null)
  const [isCommentVisible, setIsCommentVisible] = useState(false)
  const [isCommentSubmitted, setIsCommentSubmitted] = useState(false)
  const [commentValue, setCommentValue] = useState('')
  const isValidAssistantMessage = (msg: Message): msg is AssistantMessage =>
    msg.type === 'Assistant' &&
    typeof msg.span_id === 'string' &&
    msg.span_id.length > 0 &&
    typeof msg.query === 'string' &&
    msg.query.trim().length > 0
  const showFeedbackContainer = isValidAssistantMessage(message) && Boolean(feedbackText)
  const allowComments = Boolean(addCommentCtaText && feedbackPlaceholderText && feedbackConfirmationText)
  const submittedFeedbackA11ySuffix = feedbackConfirmationText ? ` ${feedbackConfirmationText}` : ''
  const thumbsUpAccessibilityLabel = isCommentSubmitted
    ? `${thumbsUpLabel}.${submittedFeedbackA11ySuffix}`
    : thumbsUpLabel
  const thumbsDownAccessibilityLabel = isCommentSubmitted
    ? `${thumbsDownLabel}.${submittedFeedbackA11ySuffix}`
    : thumbsDownLabel

  const handleFeedback = (value: 'up' | 'down') => {
    // Don't allow changing feedback after it's been submitted
    if (isCommentSubmitted) return

    const nextFeedback = feedback === value ? null : value
    setFeedback(nextFeedback)

    // If deselecting feedback, hide the comment section
    if (nextFeedback === null) {
      setIsCommentVisible(false)
    }

    if (nextFeedback === value && isValidAssistantMessage(message)) {
      onFeedback?.({ span_id: message.span_id, label: value === 'up' ? 'thumbs-up' : 'thumbs-down' })
    }
  }

  const handleCancelComment = () => {
    setIsCommentVisible(false)
  }

  /** Normalises the TextArea component's polymorphic onChange value to a plain string. */
  const handleCommentChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>) =>
    setCommentValue(typeof value === 'string' ? value : value?.target?.value ?? '')

  const handleSubmitComment = () => {
    if (!feedback || !isValidAssistantMessage(message)) return
    onFeedback?.({
      span_id: message.span_id,
      label: feedback === 'up' ? 'thumbs-up' : 'thumbs-down',
      ...(commentValue.trim() ? { comment: commentValue.trim() } : {}),
    })
    setIsCommentVisible(false)
    setIsCommentSubmitted(true)
    setCommentValue('')
  }

  return (
    <div data-message-id={message.id}>
      {/* Message Tag */}
      {message.tag && (
        <MessageTagWrapper messageType={message.type}>
          <MessageTagIconWrapper>
            <Icon
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
              icon={(message.type === 'Human' ? Profile : SparkleFilled) as any}
              variant={{ size: 'large', rank: 'primary' }}
            />
          </MessageTagIconWrapper>
          {message.tag}
        </MessageTagWrapper>
      )}

      {/* Message Content */}
      <MessageBubble messageType={message.type}>
        {renderMarkdownContent(message.content, tableAccessibilityLabel)}

        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <SourcesContainer>
            <strong>{sourcesLabel}</strong>
            {message.sources.map((source, idx) => (
              <SourceItem key={idx}>
                {source.url ? (
                  <Link href={source.url} target="_blank" rel="noopener noreferrer">
                    {source.file_name}
                    {source.page != null ? ` (Page ${source.page})` : ''}
                  </Link>
                ) : (
                  <span>
                    {source.file_name}
                    {source.page != null ? ` (Page ${source.page})` : ''}
                  </span>
                )}
              </SourceItem>
            ))}
          </SourcesContainer>
        )}
        {showFeedbackContainer && (
          <FeedbackContainer>
            <FeedbackRow>
              <FeedbackActions>
                <IconButton
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  icon={ThumbsUpFill}
                  tokens={getFeedbackIconButtonTokens(feedback === 'up')}
                  onPress={() => handleFeedback('up')}
                  accessibilityLabel={thumbsUpAccessibilityLabel}
                  accessibilityState={{ disabled: isCommentSubmitted, selected: feedback === 'up' }}
                  variant={{ size: 'large' }}
                />
                <FeedbackThumbDownOffset>
                  <IconButton
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    icon={ThumbsDownFill}
                    tokens={getFeedbackIconButtonTokens(feedback === 'down')}
                    onPress={() => handleFeedback('down')}
                    accessibilityLabel={thumbsDownAccessibilityLabel}
                    accessibilityState={{ disabled: isCommentSubmitted, selected: feedback === 'down' }}
                    variant={{ size: 'large' }}
                  />
                </FeedbackThumbDownOffset>
              </FeedbackActions>
              {allowComments && isCommentSubmitted ? (
                <FeedbackTextSubmitted>{feedbackConfirmationText}</FeedbackTextSubmitted>
              ) : !feedback || !allowComments ? (
                <FeedbackText>{feedbackText}</FeedbackText>
              ) : (
                <TextButton onPress={() => setIsCommentVisible((prev) => !prev)} tokens={{ alignSelf: 'center' }}>
                  {addCommentCtaText}
                </TextButton>
              )}
            </FeedbackRow>
            {allowComments && isCommentVisible && (
              <FeedbackCommentContainer>
                <FeedbackCommentField>
                  <TextArea value={commentValue} onChange={handleCommentChange} placeholder={feedbackPlaceholderText} />
                </FeedbackCommentField>
                <FeedbackCommentActions>
                  <Button
                    variant={{ size: 'small' }}
                    onPress={handleSubmitComment}
                    disabled={!feedback || !commentValue.trim()}
                  >
                    {commentSubmitLabel}
                  </Button>
                  <TextButton onPress={handleCancelComment} tokens={{ alignSelf: 'center' }}>
                    {commentCancelLabel}
                  </TextButton>
                </FeedbackCommentActions>
              </FeedbackCommentContainer>
            )}
          </FeedbackContainer>
        )}
      </MessageBubble>
    </div>
  )
}

export default MessageItem
